import { Apple, ArrowForward, Google } from "@mui/icons-material";
import {
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Alert, AlertTitle } from "@mui/material";
import { City, ICity, IState, State } from "country-state-city";
import { useFormik } from "formik";
import MuiPhoneNumber from "mui-phone-number";
import { FormEvent, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { EmailIcon, PasswordIcon } from "src/grading/assets";
import { LoadingButton } from "src/grading/components";
import { InputError } from "src/grading/components/InputError";
import Loader from "src/grading/components/Loader/Loader";
import {
  useLoginMutation,
  useResetPasswordMutation,
} from "src/store/reducers/auth/authApiSlice";
import {
  AcceptInvitePayload,
  useAcceptInviteMutation,
  useGetInviteQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import loginValidationSchema from "../../Login/LoginValidation";
import useStyles from "./AcceptInvite.styles";

const STEP = {
  LOGIN: "LOGIN",
  FORGOT_PASSWORD: "FORGOT_PASSWORD",
  RESET_PASSWORD: "RESET_PASSWORD",
  SIGN_UP: "SIGN_UP",
  NOT_FOUND: "NOT_FOUND",
  ACCEPTED: "ACCEPTED",
};

export const AcceptInvite = () => {
  const theme = useTheme();

  const classes = useStyles();

  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);

  const history = useHistory();

  const { data: invite, isLoading: isInviteLoading } = useGetInviteQuery({
    token: queryParams.get("token") as string,
  });

  const [login, { isLoading: isLoginLoading }] = useLoginMutation();

  const [resetPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation();

  const [acceptInvite, { isLoading: isAcceptInviteLoading }] =
    useAcceptInviteMutation();

  const [error, setError] = useState("");

  const [step, setStep] = useState(STEP.NOT_FOUND);

  const [states] = useState<IState[]>(State.getStatesOfCountry("US"));

  const [cities, setCities] = useState<ICity[]>([]);

  const handleLogin = async (email: string, password: string) => {
    try {
      const _ = await login({ email, password }).unwrap();
      setStep(STEP.SIGN_UP);
    } catch (error: any) {
      if (error.status < 500) {
        setError(error.data.message);
      } else {
        setError("Something went wrong.");
      }
    }
  };

  const handleForgotPassword = async (email: string) => {
    try {
      console.log("Hello");
      const res = await resetPassword({ email }).unwrap();

      if (res.success) {
        setStep(STEP.RESET_PASSWORD);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const handleResetPassword = async (
    email: string,
    code: string,
    newPassword: string,
  ) => {
    try {
      const res = await resetPassword({
        email,
        code,
        password: newPassword,
      }).unwrap();

      if (res.success) {
        setStep(STEP.LOGIN);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const handleAcceptInvite = async (payload: AcceptInvitePayload) => {
    try {
      const res = await acceptInvite({ payload }).unwrap();

      if (res.success) {
        setStep(STEP.ACCEPTED);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      timezone: "",
      code: "",
      newPassword: "",
      validateNewPassword: "",
      rank: "",
      jobTitle: "",
      phoneNumber: "",
      city: "",
      state: "",
    },
    validationSchema: loginValidationSchema,
    onSubmit: async (values) => {
      if (step === STEP.LOGIN) {
        handleLogin(values.email, values.password);
      }

      if (step === STEP.SIGN_UP) {
        const token = queryParams.get("token") as string;
        const registerUser = invite?.data?.profile
          ? undefined
          : {
              firstName: values.firstName,
              lastName: values.lastName,
              email: values.email,
              password: values.password,
              timezone: values.timezone,
            };

        const graderInfo =
          invite?.data?.metadata?.role === "organization_grader"
            ? {
                rank: values.rank,
                jobTitle: values.jobTitle,
                phoneNumber: values.phoneNumber,
                city: values.city,
                state: values.state,
              }
            : undefined;
        handleAcceptInvite({
          token,
          registerUser,
          graderInfo,
        });
      }
    },
  });

  const { touched, values, errors, handleSubmit, handleChange, setFieldValue } =
    formik;

  const getTitle = () => {
    switch (step) {
      case STEP.LOGIN:
        return "Login to your account";
      case STEP.FORGOT_PASSWORD:
        return "Forgot your password?";
      case STEP.SIGN_UP:
        return `${invite?.data?.metadata?.inviterName} invited you to join ${invite?.data?.metadata?.organizationName}`;
      case STEP.RESET_PASSWORD:
        return "Password reset";
      case STEP.ACCEPTED:
        return "Congrats! You are in!";
      default:
        return "Invite not found!";
    }
  };

  const getButtonCaption = () => {
    switch (invite?.data?.metadata?.role) {
      case "organization_graders":
        return "Grader Login";
      case "organization_coaches":
        return "Coach Login";
      case "organization_owners":
      case "organization_super_admins":
      case "organization_admins":
        return "Admin Login";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!isInviteLoading && invite) {
      setFieldValue(
        "timezone",
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
      setFieldValue("email", invite.data?.email || "");
      setFieldValue("phoneNumber", invite.data?.profile?.phoneNumber || "");
      setFieldValue("rank", invite.data?.profile?.graderInfo?.rank || "");
      setFieldValue(
        "jobTitle",
        invite.data?.profile?.graderInfo?.jobTitle || "",
      );
      setFieldValue("state", invite.data?.profile?.graderInfo?.state || "");
      setCities(
        City.getCitiesOfState(
          "US",
          invite.data?.profile?.graderInfo?.state || "",
        ),
      );
      setFieldValue("city", invite.data?.profile?.graderInfo?.city || "");
      if (invite?.data?.profile) {
        setStep(STEP.LOGIN);
      } else {
        setStep(STEP.SIGN_UP);
      }
    }
  }, [invite, isInviteLoading, setFieldValue]);

  return (
    <Loader loading={isInviteLoading}>
      <Grid
        container
        height={"100%"}
        maxHeight={"100vh"}
        component={"form"}
        onSubmit={(e: FormEvent) => {
          if (step === STEP.FORGOT_PASSWORD) {
            handleForgotPassword(values.email);
          }

          if (step === STEP.RESET_PASSWORD) {
            handleResetPassword(values.email, values.code, values.newPassword);
          }
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Grid container item flex={1}>
          <Grid
            flex={1}
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
          >
            <img className="logo" alt="ReadyFit Logo" src={"/logo.png"} />
            <Grid width={"20rem"} flex={1}>
              <Typography variant="h5" mb={5} align="center">
                {getTitle()}
              </Typography>
              {step === STEP.LOGIN && (
                <>
                  <Grid
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    height={"100%"}
                  >
                    <TextField
                      label="Email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange("email")}
                      style={{ paddingBottom: "20px" }}
                      InputProps={{
                        className: "text-field",
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      placeholder="Enter your email here"
                      error={InputError("email", touched, errors).error}
                      helperText={
                        InputError("email", touched, errors).helperText
                      }
                      disabled
                    />
                    <br />
                    <TextField
                      label="Password"
                      type="password"
                      value={values.password}
                      onChange={handleChange("password")}
                      style={{ paddingBottom: "20px" }}
                      InputProps={{
                        className: "text-field",
                        endAdornment: (
                          <InputAdornment position="end">
                            <PasswordIcon />
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      placeholder="Enter your password here"
                    />
                    <Typography
                      variant="body1"
                      textAlign={"right"}
                      fontWeight={"bold"}
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => {
                        setStep(STEP.FORGOT_PASSWORD);
                      }}
                    >
                      Forgot your password?
                    </Typography>
                    <br />
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      size="small"
                      fullWidth
                      className="sign-in-button"
                      type="submit"
                      onClick={() => handleSubmit()}
                      label="Sign in"
                      sx={{ marginTop: theme.spacing(8) }}
                      loading={isLoginLoading}
                      disabled={
                        !values.email ||
                        !values.password ||
                        values.email.includes("@") === false
                      }
                    />
                    {error.length > 0 && (
                      <Alert
                        severity="error"
                        style={{ marginTop: theme.spacing(2) }}
                      >
                        <AlertTitle>Error</AlertTitle>
                        {error}
                      </Alert>
                    )}
                  </Grid>
                </>
              )}
              {step === STEP.FORGOT_PASSWORD && (
                <Grid
                  display={"flex"}
                  flex={1}
                  flexDirection={"column"}
                  height={"100%"}
                >
                  <Typography variant="caption" mb={"1rem"}>
                    Enter your email address and we’ll send you a link to reset
                    your password.
                  </Typography>

                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange("email")}
                    style={{ paddingBottom: "20px" }}
                    InputProps={{
                      className: "text-field",
                      endAdornment: (
                        <InputAdornment position="end">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    placeholder="Enter your email here"
                    error={InputError("email", touched, errors).error}
                    helperText={InputError("email", touched, errors).helperText}
                    disabled
                  />
                  <br />

                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    className="sign-in-button"
                    type="submit"
                    onClick={() => handleSubmit()}
                    sx={{ marginTop: "1rem" }}
                    label="Reset my password"
                    loading={isResetPasswordLoading}
                    disabled={
                      !values.email || values.email.includes("@") === false
                    }
                  />

                  <Typography
                    variant="body1"
                    textAlign={"right"}
                    mt={"0.5rem"}
                    onClick={() => setStep(STEP.FORGOT_PASSWORD)}
                  >
                    Need help?{" "}
                    <span
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      Contact us.
                    </span>
                  </Typography>

                  {error.length > 0 && (
                    <Alert
                      severity="error"
                      style={{ marginTop: theme.spacing(2) }}
                    >
                      <AlertTitle>Error</AlertTitle>
                      {error}
                    </Alert>
                  )}

                  <Typography
                    variant="body1"
                    textAlign={"left"}
                    mt={"auto"}
                    pb={10}
                    sx={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                    onClick={() => setStep(STEP.LOGIN)}
                  >
                    Back to login
                  </Typography>
                </Grid>
              )}

              {step === STEP.RESET_PASSWORD && (
                <Grid
                  display={"flex"}
                  flex={1}
                  flexDirection={"column"}
                  height={"100%"}
                >
                  <Typography variant="caption" mb={"1rem"}>
                    We have sent a password reset code by email to{" "}
                    {values.email}. Enter it below to reset your password.
                  </Typography>
                  <TextField
                    label="Code"
                    name="code"
                    type="text"
                    value={values.code}
                    onChange={handleChange("code")}
                    style={{ paddingBottom: "20px" }}
                    InputProps={{
                      className: "text-field",
                    }}
                    fullWidth
                    placeholder="Enter your code here"
                    error={InputError("code", touched, errors).error}
                    helperText={InputError("code", touched, errors).helperText}
                  />
                  <br />
                  <TextField
                    name="newPassword"
                    label="New Password"
                    type="password"
                    value={values.newPassword}
                    onChange={handleChange("newPassword")}
                    style={{ paddingBottom: "20px" }}
                    InputProps={{
                      className: "text-field",
                      endAdornment: (
                        <InputAdornment position="end">
                          <PasswordIcon />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    placeholder="Enter your new password here"
                  />
                  <br />

                  <TextField
                    name="validateNewPassword"
                    label="Enter New Password Again"
                    type="password"
                    value={values.validateNewPassword}
                    onChange={handleChange("validateNewPassword")}
                    InputProps={{
                      className: "text-field",
                      endAdornment: (
                        <InputAdornment position="end">
                          <PasswordIcon />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    placeholder="Enter your new password again"
                  />
                  <br />

                  <Typography />
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    className="sign-in-button"
                    type="submit"
                    onClick={() => handleSubmit()}
                    label="Change Password"
                    sx={{ marginTop: theme.spacing(4) }}
                    loading={isResetPasswordLoading}
                    disabled={
                      isResetPasswordLoading ||
                      !values.code ||
                      !values.newPassword ||
                      values.newPassword !== values.validateNewPassword
                    }
                  />
                  {error.length > 0 && (
                    <Alert
                      severity="error"
                      style={{ marginTop: theme.spacing(2) }}
                    >
                      <AlertTitle>Error</AlertTitle>
                      {error}
                    </Alert>
                  )}

                  <Grid
                    container
                    mb={theme.spacing(2)}
                    mt={"auto"}
                    pb={10}
                    flexDirection={"column"}
                    rowGap={theme.spacing(1)}
                  >
                    <Typography
                      variant="body1"
                      textAlign={"left"}
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                      onClick={() => setStep(STEP.LOGIN)}
                    >
                      Back to login
                    </Typography>

                    <Typography
                      variant="body1"
                      textAlign={"left"}
                      onClick={() => setStep(STEP.RESET_PASSWORD)}
                    >
                      Need help?{" "}
                      <span
                        style={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontWeight: "bold",
                        }}
                      >
                        Contact us.
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {step === STEP.SIGN_UP && (
                <Grid
                  display={"flex"}
                  flex={1}
                  flexDirection={"column"}
                  height={"100%"}
                >
                  {(!invite?.data?.profile ||
                    invite.data?.metadata?.role === "organization_graders") && (
                    <Typography variant="caption" mb={"1rem"}>
                      Please complete the form to accept your invitation.
                    </Typography>
                  )}
                  {!invite?.data?.profile && (
                    <>
                      <TextField
                        label="First Name"
                        name="firstName"
                        type="text"
                        value={values.firstName}
                        onChange={handleChange("firstName")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("firstName", touched, errors).error}
                        helperText={
                          InputError("firstName", touched, errors).helperText
                        }
                      />
                      <TextField
                        label="Last Name"
                        name="lastName"
                        type="text"
                        value={values.lastName}
                        onChange={handleChange("lastName")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("lastName", touched, errors).error}
                        helperText={
                          InputError("lastName", touched, errors).helperText
                        }
                      />
                      <TextField
                        label="Email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange("email")}
                        style={{ paddingBottom: "20px" }}
                        disabled
                        InputProps={{
                          className: "text-field",
                          endAdornment: (
                            <InputAdornment position="end">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("email", touched, errors).error}
                        helperText={
                          InputError("email", touched, errors).helperText
                        }
                      />
                      <TextField
                        label="Password"
                        name="password"
                        type="password"
                        value={values.password}
                        onChange={handleChange("password")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                          endAdornment: (
                            <InputAdornment position="end">
                              <PasswordIcon />
                            </InputAdornment>
                          ),
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                      />
                    </>
                  )}

                  {invite?.data.metadata?.role === "organization_graders" && (
                    <>
                      <TextField
                        label="Rank"
                        name="rank"
                        type="text"
                        value={values.rank}
                        onChange={handleChange("rank")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("rank", touched, errors).error}
                        helperText={
                          InputError("rank", touched, errors).helperText
                        }
                      />
                      <TextField
                        label="Job Title"
                        name="jobTitle"
                        type="text"
                        value={values.jobTitle}
                        onChange={handleChange("jobTitle")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("jobTitle", touched, errors).error}
                        helperText={
                          InputError("jobTitle", touched, errors).helperText
                        }
                      />
                      <MuiPhoneNumber
                        label="Phone Number"
                        name="phoneNumber"
                        defaultCountry="us"
                        variant="outlined"
                        value={values.phoneNumber}
                        onChange={handleChange("phoneNumber")}
                        style={{ paddingBottom: "20px" }}
                        InputProps={{
                          className: "text-field",
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth
                        error={InputError("phoneNumber", touched, errors).error}
                        helperText={
                          InputError("phoneNumber", touched, errors).helperText
                        }
                      />
                      <FormControl fullWidth style={{ paddingBottom: "20px" }}>
                        <InputLabel shrink id="state-label">
                          State
                        </InputLabel>
                        <Select
                          notched
                          className={classes.input}
                          labelId="state-label"
                          label="State"
                          fullWidth
                          value={values.state}
                          onChange={(e) => {
                            setFieldValue("state", e.target.value);
                            setCities(
                              City.getCitiesOfState("US", e.target.value),
                            );
                          }}
                        >
                          {states.map((s) => (
                            <MenuItem key={s.isoCode} value={s.isoCode}>
                              {s.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth style={{ paddingBottom: "20px" }}>
                        <InputLabel shrink id="city-label">
                          City
                        </InputLabel>
                        <Select
                          notched
                          className={classes.input}
                          labelId="city-label"
                          label="City"
                          fullWidth
                          value={values.city}
                          onChange={(e) => {
                            setFieldValue("city", e.target.value);
                          }}
                        >
                          {cities.map((c) => (
                            <MenuItem key={c.name} value={c.name}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    className="sign-in-button"
                    type="submit"
                    onClick={() => handleSubmit()}
                    label="Accept invitation"
                    sx={{ marginTop: theme.spacing(8) }}
                    loading={isAcceptInviteLoading}
                  />
                  {error.length > 0 && (
                    <Alert
                      severity="error"
                      style={{ marginTop: theme.spacing(2) }}
                    >
                      <AlertTitle>Error</AlertTitle>
                      {error}
                    </Alert>
                  )}
                </Grid>
              )}

              {step === STEP.ACCEPTED &&
                invite?.data?.metadata?.role !== "organization_athletes" && (
                  <Grid
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    height={"100%"}
                  >
                    <Button
                      style={{ marginBottom: theme.spacing(7) }}
                      variant="contained"
                      onClick={() => {
                        history.push("/");
                      }}
                    >
                      {getButtonCaption()}
                    </Button>
                  </Grid>
                )}

              {step === STEP.ACCEPTED &&
                invite?.data?.metadata?.role === "organization_athletes" && (
                  <Grid
                    display={"flex"}
                    flex={1}
                    flexDirection={"column"}
                    height={"100%"}
                  >
                    <Typography
                      variant="h5"
                      align="center"
                      mb={theme.spacing(1)}
                    >
                      Download the App
                    </Typography>
                    <Grid
                      container
                      className={classes.storesIconContainer}
                      component={"a"}
                      href="https://apps.apple.com/es/app/readyfit-fitness-tests/id1555380908"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Apple style={{ color: "white" }} />
                      <Typography
                        variant="button"
                        color={"#ffffff"}
                        fontSize={"14px"}
                      >
                        App Store
                      </Typography>
                      <ArrowForward style={{ color: "white" }} />
                    </Grid>

                    <Grid
                      container
                      className={classes.storesIconContainer}
                      component={"a"}
                      href="https://play.google.com/store/apps/details?id=com.readyfit.ReadyFit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Google style={{ color: "white" }} />
                      <Typography
                        variant="button"
                        color={"#ffffff"}
                        fontSize={"14px"}
                      >
                        Play Store
                      </Typography>
                      <ArrowForward style={{ color: "white" }} />
                    </Grid>
                  </Grid>
                )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Loader>
  );
};
